import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { venueManagement, devices, violations } from '../services/api';
import VenueMap from './VenueMap';
import { loadGoogleMapsAPI } from '../utils/googleMaps';
import {
  MapPinIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  ChartBarIcon,
  ClockIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

interface Venue {
  id: string;
  name: string;
  address: string;
  city: string;
  country?: string;
  timezone: string;
  contact_email?: string;
  contact_phone?: string;
  latitude?: number;
  longitude?: number;
  monitoring_enabled?: boolean;
  settings?: any;
}

interface Device {
  id: string;
  device_name: string;
  location: string;
  is_online: boolean;
  last_seen: string;
}

interface Violation {
  id: string;
  device_id: string;
  time: string;
  threshold_name: string;
  metric_type: string;
  measured_value: number;
  threshold_value: number;
  duration_seconds: number;
  resolved_at?: string;
  acknowledged?: boolean;
}

const VenueDetails: React.FC = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const { user } = useAuth();
  const [venue, setVenue] = useState<Venue | null>(null);
  const [venueDevices, setVenueDevices] = useState<Device[]>([]);
  const [recentViolations, setRecentViolations] = useState<Violation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [isTogglingMonitoring, setIsTogglingMonitoring] = useState(false);

  useEffect(() => {
    if (venueId) {
      loadVenueData();
    }
  }, [venueId]);

  useEffect(() => {
    // Load Google Maps API
    loadGoogleMapsAPI()
      .then(() => setMapsLoaded(true))
      .catch(err => {
        console.warn('Google Maps failed to load:', err);
        setMapsLoaded(false);
      });
  }, []);

  const loadVenueData = async () => {
    try {
      setLoading(true);
      
      // Load venue details
      const venueResponse = await venueManagement.getVenueDetails(venueId!);
      setVenue(venueResponse.data);
      
      // Load devices
      const devicesResponse = await devices.list(venueId);
      setVenueDevices(devicesResponse.data);
      
      // Load recent violations
      const violationsResponse = await violations.list({ venue_id: venueId, limit: 5 });
      setRecentViolations(violationsResponse.data);
      
    } catch (err) {
      console.error('Failed to load venue data:', err);
      setError('Failed to load venue details');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditName = () => {
    setEditedName(venue?.name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleSaveName = async () => {
    if (!editedName.trim() || editedName === venue?.name) {
      handleCancelEditName();
      return;
    }

    try {
      setIsSavingName(true);
      const response = await venueManagement.renameVenue(venueId!, editedName.trim());
      setVenue(prev => prev ? { ...prev, name: editedName.trim() } : null);
      setIsEditingName(false);
      setEditedName('');
    } catch (err) {
      console.error('Failed to rename venue:', err);
      alert('Failed to rename venue');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleToggleMonitoring = async () => {
    if (!venue || isTogglingMonitoring) return;

    const newState = !venue.monitoring_enabled;
    const confirmMessage = newState
      ? 'Enable monitoring for this venue? Devices will start collecting measurements.'
      : 'Disable monitoring for this venue? All measurement collection will stop.';

    if (!window.confirm(confirmMessage)) return;

    try {
      setIsTogglingMonitoring(true);
      await venueManagement.toggleMonitoring(venueId!, newState);
      setVenue(prev => prev ? { ...prev, monitoring_enabled: newState } : null);
    } catch (err) {
      console.error('Failed to toggle monitoring:', err);
      alert('Failed to toggle monitoring');
    } finally {
      setIsTogglingMonitoring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !venue) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 p-4 rounded-lg">
          <p className="text-red-800">{error || 'Venue not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') handleCancelEditName();
                  }}
                  className="text-3xl font-bold px-2 py-1 border rounded focus:outline-none focus:border-indigo-500"
                  autoFocus
                  disabled={isSavingName}
                />
                <button
                  onClick={handleSaveName}
                  disabled={isSavingName}
                  className="p-2 text-green-600 hover:bg-green-50 rounded"
                >
                  <CheckIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={handleCancelEditName}
                  disabled={isSavingName}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{venue.name}</h1>
                {(user?.role === 'engineer' || user?.role === 'admin') && (
                  <button
                    onClick={handleStartEditName}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 rounded"
                    title="Rename venue"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center text-gray-600 dark:text-gray-400 mt-2">
              <MapPinIcon className="h-5 w-5 mr-2" />
              <span>{venue.address}, {venue.city}</span>
            </div>
            {venue.contact_email && (
              <p className="text-gray-600 dark:text-gray-400 mt-1">Contact: {venue.contact_email}</p>
            )}
          </div>
          <Link
            to={`/venues/${venueId}/add-device`}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Device
          </Link>
        </div>
      </div>

      {/* Monitoring Control */}
      {(user?.role === 'admin' || user?.role === 'engineer' || user?.role === 'manager') && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Venue Monitoring</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {venue.monitoring_enabled !== false
                  ? 'Monitoring is active. Devices are collecting measurements.'
                  : 'Monitoring is disabled. No measurements are being collected.'}
              </p>
            </div>
            <button
              onClick={handleToggleMonitoring}
              disabled={isTogglingMonitoring}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                venue.monitoring_enabled !== false
                  ? 'bg-indigo-600'
                  : 'bg-gray-200 dark:bg-gray-700'
              } ${isTogglingMonitoring ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={venue.monitoring_enabled !== false ? 'Disable monitoring' : 'Enable monitoring'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  venue.monitoring_enabled !== false ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <DevicePhoneMobileIcon className="h-10 w-10 text-indigo-600" />
            <div className="ml-4">
              <p className="text-2xl font-semibold">{venueDevices.length}</p>
              <p className="text-gray-600 dark:text-gray-400">Active Devices</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-10 w-10 text-yellow-500" />
            <div className="ml-4">
              <p className="text-2xl font-semibold">
                {recentViolations.filter(v => !v.resolved_at).length}
              </p>
              <p className="text-gray-600 dark:text-gray-400">Active Violations</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <ChartBarIcon className="h-10 w-10 text-green-600" />
            <div className="ml-4">
              <p className="text-2xl font-semibold">
                {venueDevices.filter(d => d.is_online).length}
              </p>
              <p className="text-gray-600 dark:text-gray-400">Online Now</p>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      {venue.address && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Location</h2>
            <button
              onClick={() => setShowMap(!showMap)}
              className="text-indigo-600 hover:text-indigo-700 text-sm"
            >
              {showMap ? 'Hide Map' : 'Show Map'}
            </button>
          </div>
          
          {showMap && mapsLoaded && (
            <div className="rounded-lg overflow-hidden">
              <VenueMap
                address={`${venue.address}, ${venue.city}${venue.country ? ', ' + venue.country : ''}`}
                latitude={venue.latitude}
                longitude={venue.longitude}
                venueName={venue.name}
                height="400px"
                zoom={15}
                interactive={false}
                showMarker={true}
              />
            </div>
          )}
          
          {!showMap && (
            <div className="text-gray-600 dark:text-gray-400">
              <div className="flex items-start">
                <MapPinIcon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p>{venue.address}</p>
                  <p>{venue.city}{venue.country ? `, ${venue.country}` : ''}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Devices Section */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Devices</h2>
        {venueDevices.length === 0 ? (
          <div className="text-center py-8">
            <DevicePhoneMobileIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">No devices configured yet</p>
            <Link
              to={`/venues/${venueId}/add-device`}
              className="text-indigo-600 hover:text-indigo-700"
            >
              Add your first device
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {venueDevices.map(device => (
              <div key={device.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium">{device.device_name}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    device.is_online 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {device.is_online ? 'Online' : 'Offline'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Location: {device.location || 'Not specified'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Last seen: {new Date(device.last_seen).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Violations */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Violations</h2>
        {recentViolations.length === 0 ? (
          <div className="text-center py-8 text-gray-600 dark:text-gray-400">
            <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <p>No violations recorded</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentViolations.map(violation => (
              <div key={violation.id} className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className={`h-5 w-5 mr-3 ${
                    violation.resolved_at ? 'text-gray-400' : 'text-yellow-500'
                  }`} />
                  <div>
                    <p className="font-medium">
                      {violation.measured_value.toFixed(1)} dB
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {new Date(violation.time).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Duration: {Math.round(violation.duration_seconds / 60)} min
                  </p>
                  {violation.resolved_at ? (
                    <span className="text-xs text-green-600">Resolved</span>
                  ) : (
                    <span className="text-xs text-yellow-600">Active</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueDetails;